import { Dispatch, PropsWithChildren, useEffect, useMemo, useReducer } from 'react';
import { createSafeContext } from '@mews/framework';
import { useGeneralEnterprisesByIds } from '../../enterpriseRelationships/queries';

export interface EnterpriseState {
    acceptedCurrencyCodes?: string[] | null;
    accountingEnvironmentCode: string;
    cityId: string;
    defaultCurrencyCode: string;
    defaultLanguageCode: string;
    id: string;
    taxEnvironmentCode: string;
}

interface EnterpriseProductState {
    centralProduct: {
        id: string;
    };
    selectedEnterpriseIds: string[];
    selectedEnterpriseGroupIds: string[];
    enterprises: EnterpriseState[];
}

interface EnterpriseProductContextType {
    state: EnterpriseProductState;
    setState: Dispatch<Partial<EnterpriseProductState>>;
    isFetching: boolean;
}

const { EnterprisesProductContextProvider: ContextProvider, useEnterprisesProductContext } = createSafeContext<
    'EnterprisesProductContext',
    EnterpriseProductContextType
>('EnterprisesProductContext');

const reducer = (state: EnterpriseProductState, action: Partial<EnterpriseProductState>) => ({
    ...state,
    ...action,
});

const EnterprisesProductContextProvider = ({ children }: PropsWithChildren<{}>) => {
    const initialState: EnterpriseProductState = {
        centralProduct: {
            id: '',
        },
        selectedEnterpriseIds: [],
        selectedEnterpriseGroupIds: [],
        enterprises: [],
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    const { data: enterprisesData, isFetching } = useGeneralEnterprisesByIds(state.selectedEnterpriseIds);

    useEffect(() => {
        if (state?.selectedEnterpriseIds && enterprisesData?.enterprises) {
            const enterprises = enterprisesData.enterprises.map((enterprise) => {
                const {
                    acceptedCurrencyCodes,
                    accountingEnvironmentCode,
                    cityId,
                    defaultCurrencyCode,
                    defaultLanguageCode,
                    id,
                    taxEnvironmentCode,
                } = enterprise;

                return {
                    acceptedCurrencyCodes,
                    accountingEnvironmentCode,
                    cityId,
                    defaultCurrencyCode,
                    defaultLanguageCode,
                    id,
                    taxEnvironmentCode,
                };
            });
            dispatch({
                enterprises,
            });
        }
    }, [enterprisesData, state.selectedEnterpriseIds]);

    const contextValue = useMemo(
        () => ({
            state,
            setState: dispatch,
            isFetching,
        }),
        [isFetching, state]
    );

    return <ContextProvider value={contextValue}>{children}</ContextProvider>;
};

export { EnterprisesProductContextProvider, useEnterprisesProductContext };
